import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  ListYourHorseLink,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import ISOSearchBar from '../ISOSearchBar/ISOSearchBar';
import { HERO_SEARCH_PLACEHOLDER } from '../../../HomePage/homeContent';

import css from './TopbarDesktop.module.css';

// "Member since" year. Migrated users' Sharetribe accounts are created at
// cutover, so createdAt would misstate their tenure — prefer the original
// signup date carried over in publicData.memberSince (a bare year like 2019
// or an ISO date string), falling back to createdAt's year. Returns null when
// neither resolves (the sub-line is omitted).
const resolveMemberSinceYear = currentUser => {
  const memberSince = currentUser?.attributes?.profile?.publicData?.memberSince;
  if (typeof memberSince === 'number' && Number.isInteger(memberSince)) {
    if (memberSince >= 1000 && memberSince <= 9999) return memberSince;
  } else if (typeof memberSince === 'string') {
    // "2019" or "2019-05-14" / "2019-05-14T00:00:00Z" — take the leading year.
    const match = memberSince.trim().match(/^(\d{4})($|[-T])/);
    if (match) return parseInt(match[1], 10);
  }
  const createdAt = currentUser?.attributes?.createdAt;
  const createdYear = createdAt ? new Date(createdAt).getFullYear() : NaN;
  return Number.isFinite(createdYear) ? createdYear : null;
};

// Header favorites heart (desktop, logged-in only). Stroke style — matches the
// mobile menu's favorites icon. Cream on the maroon bar, gold on hover/focus
// (see .favoritesLink) so it doesn't spend a second gold accent (§16.2).
const HeartIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
  </svg>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const displayName = currentUser?.attributes?.profile?.displayName;
  const memberSinceYear = resolveMemberSinceYear(currentUser);

  return (
    // contentPosition="left" pins the dropdown's right edge to the avatar
    // (right: 0) in every header context — without it, wide viewports flip the
    // placement branch and the panel hangs leftward from the label edge.
    <Menu skipFocusOnNavigation={true} contentPosition="left">
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {/* Brand ribbon across the dropdown's top edge */}
        <MenuItem key="ribbon" rootClassName={css.menuRibbon} />
        {/* Header — display name over "Member since {YYYY}" (sub-line omitted
            when neither publicData.memberSince nor createdAt resolves) */}
        <MenuItem key="header" rootClassName={css.menuHeader}>
          <span className={css.menuHeaderName}>{displayName}</span>
          {memberSinceYear ? (
            <span className={css.menuHeaderSub}>Member since {memberSinceYear}</span>
          ) : null}
        </MenuItem>
        <MenuItem key="NewListingPage">
          <ListYourHorseLink className={classNames(css.menuLink, css.menuLinkGold)}>
            + List your horse
          </ListYourHorseLink>
        </MenuItem>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="FavoriteListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoriteListingsPage'))}
            name="FavoriteListingsPage"
          >
            My favorites
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.publicProfileLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ContactPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ContactPage'))}
            name="ContactPage"
          >
            <FormattedMessage id="Topbar.contactUsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    showCreateListingsLink,
    variant,
    docked,
  } = props;
  const marketplaceName = config?.marketplaceName;

  // Homepage variant: a persistent fixed header that overlays the hero photo.
  // While the hero is in view it is transparent with NO search in the bar; once
  // scrolled past the hero (docked) it transitions to the solid maroon gradient
  // and the search bar fades/slides in. The stock superbar split is dropped —
  // the person-icon Sign In is grouped with the "List your horse" pill on the
  // right instead.
  const isHomeVariant = variant === 'homepage';

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const classes = classNames(rootClassName || css.root, className, {
    [css.homeVariant]: isHomeVariant,
    [css.docked]: isHomeVariant && docked,
  });

  // Person-icon Sign In, grouped into the right cluster on the homepage variant.
  const signInIconLink = (
    <NamedLink name="LoginPage" className={css.signInIconLink}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
      Sign In
    </NamedLink>
  );

  // Logged-in (approved Option 2): the same "List your horse" CTA as the
  // logged-out branch — inheriting the homepage transparent-outline, docked
  // outline, and non-homepage treatments — followed by the avatar menu. No
  // Inbox link: TPH conversations run over SMS.
  const actionsArea = authenticatedOnClientSide ? (
    <div className={css.actions}>
      <ListYourHorseLink className={css.ctaButton}>
        List your horse
      </ListYourHorseLink>
      <NamedLink
        name="FavoriteListingsPage"
        className={css.favoritesLink}
        aria-label="My favorites"
        title="My favorites"
      >
        <HeartIcon />
      </NamedLink>
      <ProfileMenu
        currentPage={currentPage}
        currentUser={currentUser}
        onLogout={onLogout}
        showManageListingsLink={showCreateListingsLink}
        intl={intl}
      />
    </div>
  ) : (
    <div className={css.actions}>
      <ListYourHorseLink className={css.ctaButton}>
        List your horse
      </ListYourHorseLink>
      {isHomeVariant ? signInIconLink : null}
    </div>
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      {/* Superbar — sign in (hidden on the homepage variant; Sign In moves to
          the right cluster there) */}
      {mounted && !authenticatedOnClientSide && !isHomeVariant && (
        <div className={css.superbar}>
          <div className={css.superbarContent}>
            <ul className={css.superlinks}>
              <li>
                <NamedLink name="LoginPage" className={css.superlink}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Sign In
                </NamedLink>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Main nav — logo | search | cta */}
      <div className={css.inner}>
        <LinkedLogo
          id="logo-topbar-desktop"
          className={css.brand}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {isHomeVariant ? (
          // Hidden while over the hero; revealed (display:flex) once docked via
          // the root .docked class.
          <div className={css.dockSearch}>
            {/* Docked bar reuses the hero pill's placeholder verbiage — one
                source (homeContent.js), no duplicated literal. */}
            <ISOSearchBar onSearchSubmit={onSearchSubmit} placeholder={HERO_SEARCH_PLACEHOLDER} />
          </div>
        ) : (
          <ISOSearchBar onSearchSubmit={onSearchSubmit} />
        )}

        {actionsArea}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
